import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function buildQuery(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "");
  const query = new URLSearchParams();
  entries.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => query.append(key, String(item)));
      return;
    }
    query.set(key, String(value));
  });
  return query.toString();
}

function resolveUrl(baseUrl, params) {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }
  const query = buildQuery(params);
  if (!query) {
    return baseUrl;
  }
  return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${query}`;
}

export default function useCall(initialUrl, deps = []) {
  const [requestUrl, setRequestUrl] = useState(initialUrl);
  const [response, setResponse] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const requestUrlRef = useRef(initialUrl);

  useEffect(() => {
    setRequestUrl(initialUrl);
    requestUrlRef.current = initialUrl;
  }, [initialUrl, ...deps]);

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (method, payload, queryParams) => {
      abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setError(null);
      setResponse(undefined);
      setStatus("loading");

      const url = resolveUrl(requestUrlRef.current, queryParams);
      let isHttpFail = false;

      try {
        const options = {
          method,
          signal: controller.signal,
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
          },
        };
        if (method !== "GET" && payload !== undefined) {
          options.headers["Content-Type"] = "application/json";
          options.body = JSON.stringify(payload);
        }

        const res = await fetch(url, options);
        const contentType = res.headers.get("content-type") || "";
        const isJSON = contentType.includes("application/json");
        const parsedBody = isJSON ? await res.json() : await res.text();
        const parsedResponse = isJSON
          ? parsedBody
          : {
              success: false,
              message: typeof parsedBody === "string" ? parsedBody : "Respuesta no JSON",
              raw: parsedBody,
            };
        setResponse(parsedResponse);

        if (!res.ok) {
          const message = parsedResponse?.message || `Request failed with status ${res.status}`;
          const requestError = new Error(message);
          requestError.status = res.status;
          requestError.data = parsedResponse;
          isHttpFail = true;
          setError(requestError);
          setStatus("fail");
          throw requestError;
        }

        setStatus("done");
        return parsedResponse;
      } catch (err) {
        if (err?.name === "AbortError") {
          setStatus(undefined);
          return Promise.reject(err);
        }
        setError(err);
        setStatus(isHttpFail ? "fail" : "error");
        return Promise.reject(err);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [abort],
  );

  useEffect(
    () => () => {
      abort();
    },
    [abort],
  );

  const api = useMemo(
    () => ({
      response,
      status,
      error,
      isDone: status === "done",
      isLoading: status === "loading",
      url: (newUrl) => {
        requestUrlRef.current = newUrl;
        setRequestUrl(newUrl);
      },
      $get: (params) => run("GET", undefined, params),
      $post: (payload) => run("POST", payload),
      $put: (payload) => run("PUT", payload),
      $delete: (payload) => run("DELETE", payload),
      $abort: abort,
    }),
    [abort, error, response, run, status],
  );

  return api;
}
